import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/shared";
import { Pencil, Eye, Save, ImagePlus, Trash2, X, MapPin, ExternalLink, Navigation, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { gpsToImagePct } from "@/lib/gpsTransform";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_TONES = {
  vacant:      { fill: "#3CAE5C", ring: "#1F6336", label: "Vacant" },
  occupied:    { fill: "#A13E34", ring: "#67241B", label: "Occupied" },
  reservation: { fill: "#946A00", ring: "#4F3900", label: "Reserved" },
  closed:      { fill: "#737A76", ring: "#3F4441", label: "Closed" },
  pm:          { fill: "transparent", ring: "#1F4FBE", label: "Park Model" },  // blue outline
  mh:          { fill: "transparent", ring: "#7C1850", label: "Mobile Home" }, // maroon outline
};

function legendBadge(tone) {
  const t = STATUS_TONES[tone];
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span
        className="inline-block w-3.5 h-3.5 rounded-full"
        style={{
          background: t.fill === "transparent" ? "white" : t.fill,
          border: `2px solid ${t.ring}`,
        }}
      />
      <span className="text-[#737A76]">{t.label}</span>
    </div>
  );
}

export default function ParkMapPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentPark, refresh: refreshParks } = usePark();
  const canEdit = ["master", "admin", "super_admin", "park_admin"].includes(user?.role);
  const canWalk = ["master", "admin", "super_admin", "park_admin", "staff"].includes(user?.role);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [lots, setLots] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [dragging, setDragging] = useState(null); // index of marker being dragged
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [imageUrlOpen, setImageUrlOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [snapLot, setSnapLot] = useState("");
  const [snapEnabled, setSnapEnabled] = useState(false); // OFF by default — user starts in Move mode
  const [selectedMarkerIdx, setSelectedMarkerIdx] = useState(null);
  const [findLot, setFindLot] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(null);
  const [pendingAddLot, setPendingAddLot] = useState(null); // when set, next map click places this lot #
  // Walk-mode GPS calibration & capture
  const [gpsAnchors, setGpsAnchors] = useState([]);  // persisted park.gps_anchors
  const [walkMode, setWalkMode] = useState(false);
  const [walkLot, setWalkLot] = useState("");
  const [walkBusy, setWalkBusy] = useState(false);
  const [calibrateMode, setCalibrateMode] = useState(false); // edit-mode sub-state
  const [lastGpsPing, setLastGpsPing] = useState(null);  // {lat, lng, accuracy, ts}

  const wrapRef = useRef(null);
  const didDragRef = useRef(false);

  const loadAll = async () => {
    if (!currentPark?.id) return;
    // Clear stale data before fetch so park-switch doesn't show old stats
    setLots([]);
    setMarkers([]);
    setImageUrl("");
    try {
      const [mapRes, parkRes] = await Promise.all([
        api.get(`/map?date=${date}`),
        api.get(`/parks/${currentPark.id}`),
      ]);
      setLots(mapRes.data);
      setMarkers(parkRes.data.map_markers || []);
      setImageUrl(parkRes.data.map_image_url || "");
      setImageUrlInput(parkRes.data.map_image_url || "");
      setGpsAnchors(parkRes.data.gps_anchors || []);
    } catch { toast.error("Failed to load park map"); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [date, currentPark?.id]);

  // Index lots by number for quick marker→lot lookup
  const lotsByNumber = useMemo(() => {
    const m = {};
    for (const l of lots) m[l.number] = l;
    return m;
  }, [lots]);

  // Snap a user-typed lot # to its canonical form by matching against existing lot records.
  // Tries exact match first, then zero-padded variants (e.g. "1" → "001"), then strip-leading-zero
  // (e.g. "001" → "1"). Returns the user's input unchanged if no match exists.
  const normalizeLotNumber = (raw) => {
    const s = (raw || "").trim();
    if (!s) return s;
    if (lotsByNumber[s]) return s;
    // Numeric inputs — try padded widths 2..5
    if (/^\d+$/.test(s)) {
      for (let w = 2; w <= 5; w++) {
        const padded = s.padStart(w, "0");
        if (lotsByNumber[padded]) return padded;
      }
      const stripped = String(parseInt(s, 10));
      if (lotsByNumber[stripped]) return stripped;
    }
    return s;
  };

  const stats = useMemo(() => {
    const occupied = lots.filter((l) => l.status === "occupied").length;
    const reserved = lots.filter((l) => l.status === "reservation").length;
    const closed = lots.filter((l) => l.status === "closed").length;
    return {
      total: lots.length,
      occupied,
      reserved,
      vacant: lots.length - occupied - reserved - closed,
    };
  }, [lots]);

  // ----- Editor handlers -----
  const handleMapClick = (e) => {
    if (!editMode) return;
    // Ignore the synthetic click that fires after a drag gesture
    if (didDragRef.current) { didDragRef.current = false; return; }
    if (dragging != null) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = +(((e.clientX - rect.left) / rect.width) * 100).toFixed(2);
    const y = +(((e.clientY - rect.top) / rect.height) * 100).toFixed(2);

    // ADD MODE (one-shot): user clicked "+ Add marker" — drop the pending lot # here.
    if (pendingAddLot) {
      const target = pendingAddLot;
      setMarkers((prev) => {
        // If a marker with this lot already exists, move it instead of duplicating
        const i = prev.findIndex((m) => m.lot_number === target);
        if (i >= 0) {
          const next = [...prev];
          next[i] = { ...next[i], x, y };
          return next;
        }
        return [...prev, { lot_number: target, x, y }];
      });
      // Select the newly placed marker so the user can immediately nudge it
      setMarkers((prev) => prev); // no-op to keep ordering above
      setTimeout(() => {
        setMarkers((cur) => {
          const idx = cur.findIndex((m) => m.lot_number === target);
          if (idx >= 0) setSelectedMarkerIdx(idx);
          return cur;
        });
      }, 0);
      toast.success(`Marker for lot ${target} placed — drag or arrow-key to fine-tune.`);
      setPendingAddLot(null);
      return;
    }

    // SNAP MODE: move existing marker for `snapLot` to click point (or append if missing),
    // then auto-advance numeric counters.
    if (snapEnabled && snapLot.trim()) {
      const target = normalizeLotNumber(snapLot);
      setMarkers((prev) => {
        const i = prev.findIndex((m) => m.lot_number === target);
        if (i >= 0) {
          const next = [...prev];
          next[i] = { ...next[i], x, y };
          return next;
        }
        return [...prev, { lot_number: target, x, y }];
      });
      // Auto-advance numeric lot # (preserve zero-padding)
      const numeric = /^\d+$/.test(target);
      if (numeric) {
        const padded = target.length;
        setSnapLot(String(parseInt(target, 10) + 1).padStart(padded, "0"));
      }
      return;
    }

    // MOVE MODE: empty-area click deselects current marker
    setSelectedMarkerIdx(null);
  };

  // Arrow-key nudge for selected marker (Shift = larger step)
  useEffect(() => {
    if (!editMode || selectedMarkerIdx == null) return;
    const handler = (e) => {
      const step = e.shiftKey ? 0.5 : 0.1;
      let dx = 0, dy = 0;
      if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else if (e.key === "Escape") { setSelectedMarkerIdx(null); return; }
      else return;
      e.preventDefault();
      setMarkers((prev) => {
        const next = [...prev];
        const m = next[selectedMarkerIdx];
        if (!m) return prev;
        next[selectedMarkerIdx] = {
          ...m,
          x: +Math.max(0, Math.min(100, m.x + dx)).toFixed(2),
          y: +Math.max(0, Math.min(100, m.y + dy)).toFixed(2),
        };
        return next;
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editMode, selectedMarkerIdx]);

  // Find a lot by # and pulse-highlight + scroll into view
  const findAndHighlight = () => {
    const target = findLot.trim();
    if (!target) return;
    const idx = markers.findIndex((m) => m.lot_number === target);
    if (idx < 0) {
      toast.error(`No marker for lot ${target}`);
      return;
    }
    setHighlightIdx(idx);
    setSelectedMarkerIdx(idx);
    // Scroll the wrap so the marker is roughly centered
    if (wrapRef.current) {
      const wrap = wrapRef.current;
      const rect = wrap.getBoundingClientRect();
      const cx = (markers[idx].x / 100) * rect.width + rect.left;
      const cy = (markers[idx].y / 100) * rect.height + rect.top;
      window.scrollTo({
        top: window.scrollY + cy - window.innerHeight / 2,
        left: window.scrollX + cx - window.innerWidth / 2,
        behavior: "smooth",
      });
    }
    setTimeout(() => setHighlightIdx(null), 2500);
  };

  // ----- Walk-mode GPS helpers -----
  const getCurrentGps = () =>
    new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("This device does not support geolocation."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          ts: Date.now(),
        }),
        (err) => reject(new Error(err.message || "Unable to read GPS location.")),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 2000 }
      );
    });

  // Calibration: capture GPS for the currently-selected marker and save as an anchor.
  const captureAnchor = async () => {
    if (selectedMarkerIdx == null) {
      toast.error("First click an existing marker (preferably at a recognizable corner) to anchor it.");
      return;
    }
    const m = markers[selectedMarkerIdx];
    try {
      const gps = await getCurrentGps();
      const anchor = { lat: gps.lat, lng: gps.lng, x: m.x, y: m.y, label: `Lot ${m.lot_number}` };
      const next = [...gpsAnchors, anchor].slice(-3); // keep newest 3
      setGpsAnchors(next);
      setLastGpsPing(gps);
      try {
        await api.put(`/parks/${currentPark.id}/gps-anchors`, next);
        toast.success(`Anchor ${next.length}/3 saved at lot ${m.lot_number} (±${Math.round(gps.accuracy)}m).`);
      } catch { toast.error("Anchor saved locally but couldn't sync to server."); }
    } catch (e) { toast.error(e.message); }
  };

  const clearAnchors = async () => {
    if (!window.confirm("Clear all GPS anchors? You'll need to recalibrate before using Walk Mode.")) return;
    setGpsAnchors([]);
    try { await api.put(`/parks/${currentPark.id}/gps-anchors`, []); toast.success("Anchors cleared."); }
    catch { toast.error("Couldn't clear anchors on server."); }
  };

  // Walk Mode: "I'm here" → GPS → affine → upsert marker for current walkLot.
  const walkImHere = async () => {
    const target = normalizeLotNumber(walkLot);
    if (!target) { toast.error("Enter a lot # first."); return; }
    if (gpsAnchors.length < 2) {
      toast.error("Walk Mode needs at least 2 GPS anchors. Ask an admin to calibrate first.");
      return;
    }
    setWalkBusy(true);
    try {
      const gps = await getCurrentGps();
      const pt = gpsToImagePct(gpsAnchors, gps.lat, gps.lng);
      if (!pt) { toast.error("Couldn't compute position — bad calibration?"); return; }
      const x = Math.max(0, Math.min(100, +pt.x.toFixed(2)));
      const y = Math.max(0, Math.min(100, +pt.y.toFixed(2)));
      // Optimistic local update
      setMarkers((prev) => {
        const i = prev.findIndex((m) => m.lot_number === target);
        if (i >= 0) { const next = [...prev]; next[i] = { ...next[i], x, y }; return next; }
        return [...prev, { lot_number: target, x, y }];
      });
      setLastGpsPing(gps);
      // Persist single marker
      await api.post(`/parks/${currentPark.id}/walk-marker`, { lot_number: target, x, y });
      toast.success(`Lot ${target} placed (±${Math.round(gps.accuracy)}m).`);
      // Auto-advance numeric
      if (/^\d+$/.test(target)) {
        setWalkLot(String(parseInt(target, 10) + 1).padStart(target.length, "0"));
      }
    } catch (e) { toast.error(e.message || "Walk capture failed."); }
    finally { setWalkBusy(false); }
  };

  const onMarkerDown = (idx) => (e) => {
    if (!editMode) return;
    // In snap mode or pending-add, let the click bubble up so the map's handler can fire.
    if ((snapEnabled && snapLot.trim()) || pendingAddLot) return;
    e.stopPropagation();
    setDragging(idx);
  };

  const onWrapMove = (e) => {
    if (!editMode || dragging == null) return;
    didDragRef.current = true;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMarkers((prev) => {
      const next = [...prev];
      next[dragging] = { ...next[dragging], x: +x.toFixed(2), y: +y.toFixed(2) };
      return next;
    });
  };

  const onWrapUp = () => setDragging(null);

  const removeMarker = (idx) => (e) => {
    e.stopPropagation();
    if (!editMode) return;
    if (!window.confirm(`Remove marker for lot ${markers[idx].lot_number}?`)) return;
    setMarkers((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveMarkers = async () => {
    try {
      await api.put(`/parks/${currentPark.id}/map-markers`, markers);
      toast.success(`Saved ${markers.length} markers`);
      setEditMode(false);
    } catch { toast.error("Save failed"); }
  };

  const saveImageUrl = async () => {
    try {
      await api.put(`/parks/${currentPark.id}/map-image`, { map_image_url: imageUrlInput.trim() });
      setImageUrl(imageUrlInput.trim());
      setImageUrlOpen(false);
      toast.success("Map image updated");
      refreshParks?.();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="park-map-page">
      <PageHeader
        title="Park Map"
        subtitle={`${stats.total} lots · ${stats.occupied} occupied · ${stats.vacant} vacant · ${stats.reserved} reserved`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-9 bg-white border-[#EAEAE5]" data-testid="map-date" />
            </div>
            {canEdit && !walkMode && (
              <Button
                variant={editMode ? "default" : "outline"}
                onClick={() => setEditMode(!editMode)}
                className={editMode ? "bg-[#284B3B] hover:bg-[#1E3F32] text-white" : "border-[#EAEAE5]"}
                data-testid="map-edit-toggle"
              >
                {editMode ? <Eye size={14} className="mr-1.5" /> : <Pencil size={14} className="mr-1.5" />}
                {editMode ? "View" : "Edit"}
              </Button>
            )}
            {canWalk && !editMode && (
              <Button
                variant={walkMode ? "default" : "outline"}
                onClick={() => { setWalkMode(!walkMode); setSelectedMarkerIdx(null); }}
                className={walkMode ? "bg-[#284B3B] hover:bg-[#1E3F32] text-white" : "border-[#EAEAE5]"}
                data-testid="map-walk-toggle"
              >
                <Navigation size={14} className="mr-1.5" />
                {walkMode ? "Exit Walk" : "Walk mode"}
              </Button>
            )}
            {editMode && (
              <>
                <Button onClick={saveMarkers} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="map-save-markers">
                  <Save size={14} className="mr-1.5" /> Save markers
                </Button>
                <Button variant="outline" onClick={() => setImageUrlOpen(!imageUrlOpen)} className="border-[#EAEAE5]" data-testid="map-image-toggle">
                  <ImagePlus size={14} className="mr-1.5" /> Set image
                </Button>
              </>
            )}
          </div>
        }
      />

      {editMode && (
        <Card className="border-[#284B3B]/30 bg-[#E3F0E6]/30 shadow-none mb-3" data-testid="snap-card">
          <CardContent className="p-3 flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm font-medium text-[#1A211D]">
              <input
                type="checkbox"
                checked={snapEnabled}
                onChange={(e) => setSnapEnabled(e.target.checked)}
                data-testid="snap-toggle"
                className="accent-[#284B3B]"
              />
              Snap mode (bulk place)
            </label>

            {snapEnabled && (
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Snap to lot #</Label>
                  <Input
                    value={snapLot}
                    onChange={(e) => setSnapLot(e.target.value)}
                    placeholder="e.g. 049"
                    className="w-28 h-9 bg-white border-[#EAEAE5] text-center font-mono tabular-nums"
                    data-testid="snap-lot-input"
                  />
                </div>
                <span className="text-xs text-[#737A76] pb-2">
                  Click map → moves lot {snapLot || "—"} there; counter auto-advances.
                </span>
              </div>
            )}

            <div className="h-8 w-px bg-[#EAEAE5]" />

            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Find lot #</Label>
                <Input
                  value={findLot}
                  onChange={(e) => setFindLot(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && findAndHighlight()}
                  placeholder="e.g. 248"
                  className="w-28 h-9 bg-white border-[#EAEAE5] text-center font-mono tabular-nums"
                  data-testid="find-lot-input"
                />
              </div>
              <Button
                onClick={findAndHighlight}
                variant="outline"
                className="h-9 border-[#284B3B] text-[#284B3B] hover:bg-[#E3F0E6]"
                data-testid="find-lot-btn"
              >
                Find &amp; select
              </Button>
            </div>

            <div className="h-8 w-px bg-[#EAEAE5]" />

            <Button
              onClick={() => {
                const num = window.prompt("Lot number for the new marker:");
                if (!num || !num.trim()) return;
                const canonical = normalizeLotNumber(num);
                if (canonical !== num.trim()) {
                  toast.info(`Matched existing lot "${canonical}" (you typed "${num.trim()}").`);
                }
                setPendingAddLot(canonical);
                toast.info(`Click on the map where lot ${canonical} should go.`);
              }}
              variant="outline"
              className="h-9 border-[#284B3B] text-[#284B3B] hover:bg-[#E3F0E6]"
              data-testid="add-marker-btn"
            >
              <MapPin size={14} className="mr-1.5" />
              {pendingAddLot ? `Click map to place lot ${pendingAddLot}` : "+ Add marker"}
            </Button>
            {pendingAddLot && (
              <Button
                variant="ghost"
                onClick={() => setPendingAddLot(null)}
                className="h-9 text-[#A13E34] hover:bg-[#FFE4E1]"
                data-testid="add-marker-cancel"
              >
                Cancel
              </Button>
            )}

            {selectedMarkerIdx != null && markers[selectedMarkerIdx] && (
              <div className="ml-auto flex items-center gap-3 text-xs">
                <div className="font-mono tabular-nums text-[#1A211D]" data-testid="selected-marker-readout">
                  <span className="text-[#737A76] mr-1">Lot</span>
                  <strong>{markers[selectedMarkerIdx].lot_number}</strong>
                  <span className="text-[#737A76] mx-2">·</span>
                  X {markers[selectedMarkerIdx].x.toFixed(2)}%
                  <span className="text-[#737A76] mx-1">·</span>
                  Y {markers[selectedMarkerIdx].y.toFixed(2)}%
                </div>
                <span className="text-[#737A76]">
                  Arrow keys nudge (Shift = bigger). Esc to deselect.
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {editMode && imageUrlOpen && (
        <Card className="border-[#EAEAE5] shadow-none mb-3" data-testid="map-image-card">
          <CardContent className="p-4 flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Map image URL</Label>
              <Input value={imageUrlInput} onChange={(e) => setImageUrlInput(e.target.value)} placeholder="https://…/parkmap.png" className="bg-white border-[#EAEAE5]" data-testid="map-image-input" />
            </div>
            <Button onClick={saveImageUrl} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="map-image-save">Save URL</Button>
          </CardContent>
        </Card>
      )}

      {editMode && (
        <Card className="border-[#284B3B]/30 bg-white shadow-none mb-3" data-testid="calibrate-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium text-[#1A211D]">
                <Crosshair size={16} className="text-[#284B3B]" />
                GPS calibration for Walk Mode
              </div>
              <span className="text-xs text-[#737A76]">
                Stand at a recognizable lot, click its marker on the map, then tap <strong>Capture GPS</strong>. Repeat for 2–3 anchors at distant corners.
              </span>
              <div className="ml-auto flex items-center gap-2">
                <span
                  className={`text-xs font-mono tabular-nums px-2 py-1 rounded ${
                    gpsAnchors.length >= 2 ? "bg-[#E3F0E6] text-[#1F6336]" : "bg-[#FFF4D4] text-[#946A00]"
                  }`}
                  data-testid="anchor-count"
                >
                  {gpsAnchors.length}/3 anchors
                </span>
                <Button
                  onClick={captureAnchor}
                  variant="outline"
                  className="h-9 border-[#284B3B] text-[#284B3B] hover:bg-[#E3F0E6]"
                  data-testid="capture-anchor-btn"
                >
                  <Crosshair size={14} className="mr-1.5" />
                  Capture GPS here
                </Button>
                {gpsAnchors.length > 0 && (
                  <Button
                    onClick={clearAnchors}
                    variant="ghost"
                    className="h-9 text-[#A13E34] hover:bg-[#FFE4E1]"
                    data-testid="clear-anchors-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            {gpsAnchors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="anchor-chips">
                {gpsAnchors.map((a, i) => (
                  <span key={i} className="text-[11px] font-mono tabular-nums bg-[#E3F0E6] text-[#1F6336] px-2 py-0.5 rounded">
                    {a.label || `#${i + 1}`} · {a.lat.toFixed(5)}, {a.lng.toFixed(5)} → {a.x.toFixed(1)}%, {a.y.toFixed(1)}%
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {walkMode && (
        <Card className="border-[#284B3B] bg-[#E3F0E6]/40 shadow-none mb-3" data-testid="walk-card">
          <CardContent className="p-4">
            {gpsAnchors.length < 2 ? (
              <div className="text-sm text-[#A13E34]" data-testid="walk-needs-calibration">
                Walk Mode needs at least 2 GPS anchors. An admin must enter <strong>Edit → Calibrate</strong> first.
              </div>
            ) : (
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#737A76]">Stand at lot #</Label>
                  <Input
                    value={walkLot}
                    onChange={(e) => setWalkLot(e.target.value)}
                    placeholder="e.g. 248"
                    className="w-32 h-12 bg-white border-[#284B3B] text-center font-mono tabular-nums text-lg"
                    data-testid="walk-lot-input"
                    inputMode="numeric"
                  />
                </div>
                <Button
                  onClick={walkImHere}
                  disabled={walkBusy || !walkLot.trim()}
                  className="h-12 px-6 bg-[#284B3B] hover:bg-[#1E3F32] text-white text-base disabled:opacity-50"
                  data-testid="walk-im-here-btn"
                >
                  <Navigation size={16} className="mr-2" />
                  {walkBusy ? "Locating…" : "I'm here — place marker"}
                </Button>
                {lastGpsPing && (
                  <div className="text-xs font-mono tabular-nums text-[#737A76]" data-testid="walk-last-ping">
                    Last GPS: {lastGpsPing.lat.toFixed(5)}, {lastGpsPing.lng.toFixed(5)} · ±{Math.round(lastGpsPing.accuracy)}m
                  </div>
                )}
                <span className="ml-auto text-xs text-[#284B3B] font-medium">
                  {gpsAnchors.length}/3 anchors active · counter auto-advances
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-[#EAEAE5] shadow-none mb-3">
        <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            {legendBadge("vacant")}
            {legendBadge("occupied")}
            {legendBadge("reservation")}
            {legendBadge("closed")}
            {legendBadge("pm")}
            {legendBadge("mh")}
          </div>
          {editMode && (
            <div className="text-xs text-[#946A00] bg-[#FFF4D4] px-3 py-1.5 rounded-md">
              <strong>Move mode:</strong> click a marker to select → drag, or use arrow keys (Shift = bigger). <strong>Snap mode:</strong> toggle on + enter lot # → click map jumps that marker there.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden">
        <CardContent className="p-0">
          {!imageUrl ? (
            <div className="p-12 text-center text-[#737A76]" data-testid="map-empty">
              <MapPin className="mx-auto mb-2 text-[#A8B0AB]" size={28} />
              <div className="text-sm">No map image set for this park yet.</div>
              {canEdit && <div className="text-xs mt-2">Click Edit → Set image to upload one.</div>}
            </div>
          ) : (
            <div
              ref={wrapRef}
              className="relative select-none"
              onClickCapture={handleMapClick}
              onMouseMove={onWrapMove}
              onMouseUp={onWrapUp}
              onMouseLeave={onWrapUp}
              style={{ cursor: editMode ? (dragging != null ? "grabbing" : (pendingAddLot ? "copy" : "crosshair")) : "default" }}
              data-testid="map-canvas"
            >
              <img
                src={imageUrl}
                alt="Park map"
                className="block w-full h-auto"
                draggable={false}
              />

              {markers.map((m, idx) => {
                const lot = lotsByNumber[m.lot_number];
                const status = lot?.status || "vacant";
                const tone = STATUS_TONES[status] || STATUS_TONES.vacant;
                // Show PM/MH outline for special types when vacant
                let outlineTone = null;
                if (status === "vacant" && lot) {
                  if (lot.lot_type?.startsWith("Park Model")) outlineTone = STATUS_TONES.pm;
                  else if (lot.lot_type?.startsWith("Mobile Home")) outlineTone = STATUS_TONES.mh;
                }
                return (
                  <Marker
                    key={`${m.lot_number}-${idx}`}
                    marker={m}
                    idx={idx}
                    lot={lot}
                    tone={outlineTone || tone}
                    editMode={editMode}
                    snapMode={editMode && ((snapEnabled && !!snapLot.trim()) || !!pendingAddLot)}
                    selected={editMode && selectedMarkerIdx === idx}
                    highlight={highlightIdx === idx}
                    onMouseDown={onMarkerDown(idx)}
                    onRemove={removeMarker(idx)}
                    onClick={(e) => {
                      // Let snap-mode / pending-add clicks bubble up to map handler
                      if (editMode && ((snapEnabled && snapLot.trim()) || pendingAddLot)) return;
                      e.stopPropagation();
                      if (editMode) {
                        // Move mode: select this marker for arrow-key nudging
                        setSelectedMarkerIdx(idx);
                        return;
                      }
                      setSelectedLot({ marker: m, lot });
                    }}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedLot} onOpenChange={(o) => !o && setSelectedLot(null)}>
        <DialogContent className="!bg-white border-[#EAEAE5] max-w-md" data-testid="lot-detail-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }} data-testid="lot-detail-title">
              Lot {selectedLot?.marker?.lot_number}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#737A76]">
              {selectedLot?.lot ? selectedLot.lot.lot_type : "No lot record for this marker"}
            </DialogDescription>
          </DialogHeader>
          {selectedLot?.lot ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Status" value={lotStatusLabel(selectedLot.lot.status)} />
                <Field label="Size" value={selectedLot.lot.size || "—"} />
                <Field label="Pad" value={selectedLot.lot.pad_size || "—"} />
                <Field label="Monthly rent" value={selectedLot.lot.monthly_rent ? `$${selectedLot.lot.monthly_rent.toFixed(2)}` : "—"} />
              </div>
              {selectedLot.lot.tenant_name && (
                <div className="border-t border-[#F0F0EC] pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#737A76]">Current tenant</div>
                  <div className="text-base font-medium" style={{ fontFamily: "Outfit" }}>{selectedLot.lot.tenant_name}</div>
                  {selectedLot.lot.reservation_id && (
                    <button
                      onClick={() => navigate(`/reservations`)}
                      className="text-xs text-[#284B3B] hover:underline inline-flex items-center gap-1 mt-1"
                      data-testid="lot-detail-view-reservation"
                    >
                      View reservation <ExternalLink size={11} />
                    </button>
                  )}
                </div>
              )}
              <div className="text-xs text-[#737A76]">
                <strong>Amenities:</strong>{" "}
                {[
                  selectedLot.lot.has_electric && "Electric",
                  selectedLot.lot.has_water && "Water",
                  selectedLot.lot.has_sewer && "Sewer",
                  selectedLot.lot.has_cable && "Cable",
                  selectedLot.lot.has_wifi && "Wi-Fi",
                  selectedLot.lot.has_bbq && "BBQ",
                ].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#A13E34] py-4">
              Marker references lot # <strong>{selectedLot?.marker?.lot_number}</strong>, but no matching lot exists in the database.
              {canEdit && " Add it from the Lots page or remove this marker."}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedLot(null)} data-testid="lot-detail-close">Close</Button>
            {selectedLot?.lot && (
              <Button
                className="bg-[#284B3B] hover:bg-[#1E3F32] text-white"
                onClick={() => navigate(`/lots?focus=${selectedLot.lot.id}`)}
                data-testid="lot-detail-open"
              >
                Open in Lots
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#737A76]">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function lotStatusLabel(s) {
  if (s === "occupied") return "Occupied";
  if (s === "reservation") return "Reserved";
  if (s === "closed") return "Closed";
  return "Vacant";
}

function Marker({ marker, idx, lot, tone, editMode, snapMode, selected, highlight, onMouseDown, onRemove, onClick }) {
  const baseSize = selected ? 22 : 16;
  const isOutline = tone.fill === "transparent";
  return (
    <div
      className="absolute group"
      style={{
        left: `${marker.x}%`,
        top: `${marker.y}%`,
        transform: "translate(-50%,-50%)",
        zIndex: selected ? 25 : highlight ? 24 : "auto",
      }}
      data-testid={`map-marker-${marker.lot_number}`}
    >
      {/* Pulsing find-highlight ring */}
      {highlight && (
        <span
          className="absolute rounded-full pointer-events-none"
          style={{
            left: "50%",
            top: "50%",
            width: 40,
            height: 40,
            transform: "translate(-50%,-50%)",
            border: "3px solid #284B3B",
            animation: "park-marker-pulse 1s ease-out infinite",
          }}
        />
      )}
      <button
        className="block rounded-full focus:outline-none"
        style={{
          width: baseSize,
          height: baseSize,
          background: isOutline ? "white" : tone.fill,
          border: selected
            ? `3px solid #284B3B`
            : `2px solid ${tone.ring}`,
          cursor: snapMode ? "crosshair" : (editMode ? "grab" : "pointer"),
          boxShadow: selected
            ? "0 0 0 3px #FFF4D4, 0 2px 6px rgba(0,0,0,.35)"
            : "0 1px 2px rgba(0,0,0,.25)",
          pointerEvents: snapMode ? "none" : "auto",
          transition: "width 120ms, height 120ms",
        }}
        onMouseDown={onMouseDown}
        onClick={onClick}
        aria-label={`Lot ${marker.lot_number}`}
        data-testid={`map-marker-${marker.lot_number}-btn`}
      />
      {/* Always-visible lot # label when selected; tooltip otherwise */}
      {selected ? (
        <div
          className="absolute z-30 pointer-events-none"
          style={{ left: "50%", top: -10, transform: "translate(-50%,-100%)" }}
        >
          <div className="bg-[#284B3B] text-white text-[11px] font-semibold px-2 py-0.5 rounded shadow-md whitespace-nowrap tabular-nums">
            Lot {marker.lot_number}
          </div>
        </div>
      ) : (
        <div
          className="absolute z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: "50%", top: -8, transform: "translate(-50%,-100%)" }}
        >
          <div className="bg-[#1A211D] text-white text-[10px] px-2 py-1 rounded shadow-md whitespace-nowrap">
            <div className="font-semibold tabular-nums">Lot {marker.lot_number}</div>
            {lot ? (
              <>
                <div className="text-[#A8B0AB]">{lot.lot_type}</div>
                {lot.tenant_name ? <div>{lot.tenant_name}</div> : null}
              </>
            ) : (
              <div className="text-[#FFB4A8]">No lot record</div>
            )}
          </div>
        </div>
      )}
      {editMode && !snapMode && (
        <button
          onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-[#A13E34] text-[#A13E34] flex items-center justify-center shadow-sm"
          aria-label="Remove"
          data-testid={`map-marker-${marker.lot_number}-remove`}
        >
          <X size={9} strokeWidth={3} />
        </button>
      )}
    </div>
  );
}
